import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const TYPE_COLORS = {
  council: 'bg-blue-100 text-blue-700',
  executive: 'bg-purple-100 text-purple-700',
  quarterly: 'bg-green-100 text-green-700',
  annual: 'bg-indigo-100 text-indigo-700',
  special: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-700',
};

export default function MemberCouncilReport() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchReports = useCallback(() => {
    setLoading(true);
    api.get('/council-reports/')
      .then(r => setReports(r.data.results || r.data))
      .catch(() => toast.error('Failed to load reports'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const filtered = reports.filter(r =>
    !search ||
    r.title.toLowerCase().includes(search.toLowerCase()) ||
    (r.report_type_display || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Council Reports</h1>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input type="text" placeholder="Search reports..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent" />
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#0B5D1E] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-500 mb-2">No Reports Available</h2>
          <p className="text-gray-400">There are no published council reports at the moment.</p>
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((r, idx) => (
            <div key={r.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition relative">
              <div className="absolute top-3 left-3 bg-[#0B5D1E] text-white text-[10px] font-bold w-6 h-6 rounded-full flex items-center justify-center">{idx + 1}</div>
              <div className="ml-8 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800 truncate">{r.title}</h3>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${TYPE_COLORS[r.report_type] || 'bg-gray-100 text-gray-600'}`}>
                    {r.report_type_display || r.report_type}
                  </span>
                </div>
                <div className="text-xs text-gray-500 space-y-1">
                  <p><span className="font-medium text-gray-600">Date:</span> {r.meeting_date}</p>
                  {r.description && <p className="line-clamp-2">{r.description}</p>}
                </div>
                {r.file && (
                  <a href={r.file} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B5D1E] text-white rounded-lg text-xs font-semibold hover:bg-[#094a18] transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
